"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  PrintButton,
  CopyButton,
  PrintFooter,
  DataReviewedNote,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* First-aid needs assessment — Health and Safety (First-Aid) Regulations 1981
   + HSE guidance L74 / leaflet INDG214.

   THERE ARE NO FIXED LEGAL RATIOS. The regulations require provision that is
   "adequate and appropriate in the circumstances" (reg 3) and an employer
   needs-assessment decides what that is. The figures below are HSE's *suggested*
   numbers from INDG214 Table 2, which the employer must still judge against
   their own circumstances.

   INDG214 Table 2 (verified 6 June 2026 against
   hse.gov.uk/pubns/indg214.pdf, INDG214(rev2)):

   Low-hazard (offices, shops, libraries):
     <25       at least one appointed person
     25–50     at least one EFAW first-aider
     >50       at least one EFAW or FAW first-aider, + one more per 100 (or part)

   Higher-hazard (construction, dangerous machinery, chemical manufacture):
     <5        at least one appointed person
     5–50      at least one FAW first-aider (EFAW or FAW per injury type)
     >50       at least one FAW first-aider for every 50 employed (or part)

   Table 2 also notes that special circumstances — shift work, sites with
   several buildings — usually need MORE personnel than the table, and provision
   must be increased to cover absences.

   First-aid kit sizing follows BS 8599-1:2019 (a benchmark, not law):
     Low-hazard:  <25 small, 25–100 medium, >100 large (1 per 100)
     High-hazard: <5 small, 5–25 medium, >25 large (1 large per 25)
   plus a critical-injury kit for cutting/dangerous-machinery/construction work
   and a personal-issue kit for lone/remote workers. */

type Risk = "" | "low" | "higher";
type Spread = "compact" | "dispersed";
type Care = "near" | "remote";

interface Provision {
  appointedPersons: number;
  fawFirstAiders: number;
  efawFirstAiders: number;
  /** "FAW" | "EFAW" | "none" — the headline trained-first-aider type. */
  trainedType: "FAW" | "EFAW" | "none";
  kit: string;
  criticalKit: boolean;
  personalKits: boolean;
}

interface Outcome {
  level: Verdict;
  title: string;
  summary: string;
  provision: Provision;
  notes: string[];
}

/* Base suggested provision from headcount + hazard (INDG214 Table 2). */
function baseProvision(people: number, risk: Exclude<Risk, "">): Provision {
  if (risk === "higher") {
    if (people < 5) {
      return {
        appointedPersons: 1,
        fawFirstAiders: 0,
        efawFirstAiders: 0,
        trainedType: "none",
        kit: "1 small first-aid kit",
        criticalKit: true,
        personalKits: false,
      };
    }
    if (people <= 50) {
      return {
        appointedPersons: 0,
        fawFirstAiders: 1,
        efawFirstAiders: 0,
        trainedType: "FAW",
        kit: people <= 25 ? "1 medium first-aid kit" : `${Math.ceil(people / 25)} large first-aid kits`,
        criticalKit: true,
        personalKits: false,
      };
    }
    // >50 higher-hazard: one FAW per 50 (or part).
    const faw = Math.ceil(people / 50);
    return {
      appointedPersons: 0,
      fawFirstAiders: faw,
      efawFirstAiders: 0,
      trainedType: "FAW",
      kit: `${Math.ceil(people / 25)} large first-aid kits (about 1 per 25 people)`,
      criticalKit: true,
      personalKits: false,
    };
  }

  // Low-hazard.
  if (people < 25) {
    return {
      appointedPersons: 1,
      fawFirstAiders: 0,
      efawFirstAiders: 0,
      trainedType: "none",
      kit: "1 small first-aid kit",
      criticalKit: false,
      personalKits: false,
    };
  }
  if (people <= 50) {
    return {
      appointedPersons: 0,
      fawFirstAiders: 0,
      efawFirstAiders: 1,
      trainedType: "EFAW",
      kit: "1 medium first-aid kit",
      criticalKit: false,
      personalKits: false,
    };
  }
  // >50 low-hazard: at least one EFAW/FAW + one more per 100 (or part).
  const efaw = Math.max(1, Math.ceil(people / 100));
  return {
    appointedPersons: 0,
    fawFirstAiders: 0,
    efawFirstAiders: efaw,
    trainedType: "EFAW",
    kit: people <= 100 ? "1 medium first-aid kit" : `${Math.ceil(people / 100)} large first-aid kits`,
    criticalKit: false,
    personalKits: false,
  };
}

function buildOutcome(
  people: number,
  risk: Exclude<Risk, "">,
  spread: Spread,
  loneWorking: boolean,
  publicInterface: boolean,
  care: Care,
): Outcome {
  const provision = baseProvision(people, risk);
  const notes: string[] = [];

  // Dispersed site → a first-aider reachable in each work area, so the count
  // can't simply be the site total if areas are far apart.
  if (spread === "dispersed") {
    if (provision.trainedType !== "none") {
      provision.fawFirstAiders = Math.max(provision.fawFirstAiders, provision.fawFirstAiders > 0 ? 2 : 0);
      provision.efawFirstAiders = Math.max(provision.efawFirstAiders, provision.efawFirstAiders > 0 ? 2 : 0);
    }
    notes.push(
      "Dispersed or multiple work areas: a trained first-aider (or appointed person) needs to be reachable within each separate area or building — provide cover per area, not just a single site total. INDG214 specifically flags sites with several buildings as needing more personnel than the table.",
    );
  }

  // Lone / remote working → extra provision and personal-issue kits.
  if (loneWorking) {
    provision.personalKits = true;
    notes.push(
      "Lone or remote working: people working alone need a way to summon help and their own first-aid means. Provide a personal-issue first-aid kit (introduced in BS 8599-1 for lone workers) and a means of raising the alarm; consider extra EFAW/FAW training for those who may have to self-treat before help arrives.",
    );
  }

  // Public interface → HSE recommends considering provision for non-employees too.
  if (publicInterface) {
    notes.push(
      "Members of the public on or near the work: the regulations only require first aid for your employees, but HSE strongly recommends including non-employees in your assessment. Where the public are present, increase provision and kit accordingly.",
    );
  }

  // Remote from emergency care → more, and more highly trained, first-aiders.
  if (care === "remote") {
    if (provision.trainedType === "EFAW") provision.trainedType = "FAW";
    if (provision.fawFirstAiders === 0 && provision.efawFirstAiders === 0 && provision.appointedPersons > 0) {
      // Upgrade an appointed-person-only site to having at least one trained first-aider.
      provision.fawFirstAiders = 1;
      provision.appointedPersons = 0;
      provision.trainedType = "FAW";
    } else {
      provision.fawFirstAiders = Math.max(provision.fawFirstAiders, provision.efawFirstAiders);
      provision.efawFirstAiders = 0;
    }
    provision.criticalKit = true;
    notes.push(
      "Remote from A&E / slow ambulance access: where help will take longer to arrive, HSE expects more first-aiders, FAW-level training rather than EFAW, and additional equipment. Agree access arrangements and brief the emergency services in advance.",
    );
  }

  // Appointed persons for cover — always called out so there's no gap when the
  // trained first-aider is absent (annual leave, sickness, breaks).
  const hasTrained = provision.fawFirstAiders + provision.efawFirstAiders > 0;
  if (hasTrained && provision.appointedPersons === 0) {
    provision.appointedPersons = 1;
    notes.push(
      "Cover for absence: name at least one appointed person to take charge if the first-aider is off site or unavailable. A first-aider's annual leave doesn't count as 'temporary and exceptional' — plan permanent cover, and add provision if you run shifts.",
    );
  }

  const totalTrained = provision.fawFirstAiders + provision.efawFirstAiders;

  let level: Verdict;
  let title: string;
  if (risk === "higher") {
    level = totalTrained === 0 ? "medium" : "high";
  } else {
    level = totalTrained === 0 ? "low" : "medium";
  }

  if (totalTrained > 0) {
    const parts: string[] = [];
    if (provision.fawFirstAiders > 0)
      parts.push(`${provision.fawFirstAiders} FAW first-aider${provision.fawFirstAiders > 1 ? "s" : ""}`);
    if (provision.efawFirstAiders > 0)
      parts.push(`${provision.efawFirstAiders} EFAW first-aider${provision.efawFirstAiders > 1 ? "s" : ""}`);
    title = `Suggested minimum: ${parts.join(" + ")}`;
  } else {
    title = "Suggested minimum: at least one appointed person";
  }

  const summary =
    risk === "higher"
      ? "Construction is treated as higher-hazard work, so HSE's suggested provision is FAW-level first aiders rather than EFAW. These are suggestions from the needs-assessment guidance, not legal minimums — you must assess your own circumstances and provide what is adequate and appropriate."
      : "These are HSE's suggested figures for lower-hazard work (office, yard or similar). They are a starting point for your needs assessment, not legal minimums — you decide what is adequate and appropriate for your circumstances.";

  return { level, title, summary, provision, notes };
}

const RISK_LABEL: Record<Exclude<Risk, "">, string> = {
  low: "Lower-hazard (office / yard / similar)",
  higher: "Higher-hazard (construction / dangerous machinery)",
};

export function FirstAidNeedsCalculator() {
  const [peopleStr, setPeopleStr] = useState("");
  const [risk, setRisk] = useState<Risk>("higher");
  const [spread, setSpread] = useState<Spread>("compact");
  const [loneWorking, setLoneWorking] = useState(false);
  const [publicInterface, setPublicInterface] = useState(false);
  const [care, setCare] = useState<Care>("near");
  const [siteName, setSiteName] = useState("");
  const resultTracked = useRef(false);

  const people = useMemo(() => {
    const n = parseInt(peopleStr, 10);
    return isFinite(n) && n > 0 ? n : null;
  }, [peopleStr]);

  const outcome = useMemo(() => {
    if (people === null || risk === "") return null;
    return buildOutcome(people, risk, spread, loneWorking, publicInterface, care);
  }, [people, risk, spread, loneWorking, publicInterface, care]);

  useEffect(() => {
    if (outcome && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("first-aid-needs-calculator", {
        people,
        risk,
        firstAiders: outcome.provision.fawFirstAiders + outcome.provision.efawFirstAiders,
      });
    }
  }, [outcome, people, risk]);

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const buildCopyText = () => {
    if (!outcome) return "";
    const p = outcome.provision;
    const lines: string[] = [];
    lines.push("FIRST AID NEEDS ASSESSMENT");
    if (siteName) lines.push(`Site / workplace: ${siteName}`);
    lines.push(`Date: ${today}`);
    lines.push("");
    lines.push(`People on site (incl. subcontractors): ${people}`);
    lines.push(`Hazard level: ${RISK_LABEL[risk as Exclude<Risk, "">]}`);
    lines.push(`Site spread: ${spread === "compact" ? "Single compact site" : "Dispersed / multiple work areas"}`);
    lines.push(`Lone or remote working: ${loneWorking ? "Yes" : "No"}`);
    lines.push(`Public present: ${publicInterface ? "Yes" : "No"}`);
    lines.push(`Distance to emergency care: ${care === "near" ? "Near" : "Remote / slow access"}`);
    lines.push("");
    lines.push("SUGGESTED MINIMUM PROVISION (HSE INDG214 — suggestions, not legal minimums):");
    if (p.fawFirstAiders > 0) lines.push(`- FAW (3-day) first-aiders: ${p.fawFirstAiders}`);
    if (p.efawFirstAiders > 0) lines.push(`- EFAW (1-day) first-aiders: ${p.efawFirstAiders}`);
    if (p.appointedPersons > 0) lines.push(`- Appointed persons (incl. absence cover): ${p.appointedPersons}`);
    lines.push(`- First-aid kit (BS 8599-1): ${p.kit}`);
    if (p.criticalKit) lines.push("- Critical-injury / catastrophic-bleed kit on site");
    if (p.personalKits) lines.push("- Personal-issue kit for each lone/remote worker");
    if (outcome.notes.length) {
      lines.push("");
      lines.push("ADJUSTMENTS FOR THIS SITE:");
      outcome.notes.forEach((n) => lines.push(`- ${n}`));
    }
    lines.push("");
    lines.push("HSE gives no fixed legal ratios. The employer must assess and provide what is adequate and appropriate.");
    lines.push("Generated with the free first aid needs calculator at ramsdocs.co.uk");
    return lines.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Your site &amp; workforce</h2>
        <p className="mt-1 text-sm text-muted">
          Answer for the workplace as a whole. There are no fixed legal numbers — this works through HSE&apos;s
          suggested figures so you have a starting point for your own assessment.
        </p>

        <div className="mt-5 space-y-5">
          <Field
            label="People on site at once"
            hint="Include your own staff, subcontractors and anyone else working under your control."
          >
            <NumberInput
              min={1}
              step={1}
              value={peopleStr}
              onChange={(e) => setPeopleStr(e.target.value)}
              placeholder="e.g. 12"
            />
          </Field>

          <Field
            label="Hazard level of the work"
            hint="Construction is treated as higher-hazard by HSE — but you can pick lower-hazard for an office or yard."
          >
            <SelectInput value={risk} onChange={(e) => setRisk(e.target.value as Risk)}>
              <option value="higher">{RISK_LABEL.higher}</option>
              <option value="low">{RISK_LABEL.low}</option>
            </SelectInput>
          </Field>

          <Field label="Site spread">
            <SelectInput value={spread} onChange={(e) => setSpread(e.target.value as Spread)}>
              <option value="compact">Single compact site — everyone close together</option>
              <option value="dispersed">Dispersed / multiple work areas or buildings</option>
            </SelectInput>
          </Field>

          <Field label="Distance / time to emergency care">
            <SelectInput value={care} onChange={(e) => setCare(e.target.value as Care)}>
              <option value="near">Near — A&amp;E and ambulance access close by</option>
              <option value="remote">Remote — slow ambulance access or far from A&amp;E</option>
            </SelectInput>
          </Field>

          <Field label="Other factors that raise provision">
            <div className="space-y-2.5">
              <CheckboxRow checked={loneWorking} onChange={setLoneWorking}>
                Anyone working <strong>alone or remotely</strong> (away from the main work area).
              </CheckboxRow>
              <CheckboxRow checked={publicInterface} onChange={setPublicInterface}>
                Members of the <strong>public</strong> are on or near the work.
              </CheckboxRow>
            </div>
          </Field>

          <Field label="" className="min-w-[200px]">
            <TextInput
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Site / workplace name (for the printed record)"
            />
          </Field>
        </div>

        {!outcome && (
          <p className="mt-5 rounded-lg bg-mist px-4 py-3 text-sm text-muted">
            Enter the number of people on site to see the suggested first-aid provision.
          </p>
        )}
      </div>

      {/* Result + printable needs assessment */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="first-aid-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                First aid needs assessment
              </div>
              <h2 className="mt-1 text-lg">
                {siteName ? `${siteName} — ` : ""}
                {today}
              </h2>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.title}>
                  {outcome.summary}
                </ResultBanner>
              </div>

              <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">Suggested minimum provision</h3>
              <table className="mt-3 w-full text-sm">
                <tbody>
                  {outcome.provision.fawFirstAiders > 0 && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">FAW first-aiders (3-day)</td>
                      <td className="py-2 text-right font-display text-lg font-bold text-emerald-deep">
                        {outcome.provision.fawFirstAiders}
                      </td>
                    </tr>
                  )}
                  {outcome.provision.efawFirstAiders > 0 && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">EFAW first-aiders (1-day)</td>
                      <td className="py-2 text-right font-display text-lg font-bold text-emerald-deep">
                        {outcome.provision.efawFirstAiders}
                      </td>
                    </tr>
                  )}
                  {outcome.provision.appointedPersons > 0 && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">
                        Appointed person{outcome.provision.appointedPersons > 1 ? "s" : ""}
                        <span className="block text-[0.78rem] text-muted">takes charge / covers absence</span>
                      </td>
                      <td className="py-2 text-right font-display text-lg font-bold text-ink">
                        {outcome.provision.appointedPersons}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-ink">First-aid kit (BS 8599-1)</td>
                    <td className="py-2 text-right text-sm font-semibold text-ink">{outcome.provision.kit}</td>
                  </tr>
                  {outcome.provision.criticalKit && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">Critical-injury kit</td>
                      <td className="py-2 text-right text-sm font-semibold text-ink">Recommended on site</td>
                    </tr>
                  )}
                  {outcome.provision.personalKits && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">Personal-issue kit</td>
                      <td className="py-2 text-right text-sm font-semibold text-ink">Per lone / remote worker</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {outcome.notes.length > 0 && (
                <>
                  <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">Adjustments for this site</h3>
                  <ul className="mt-3 space-y-2.5">
                    {outcome.notes.map((n, i) => (
                      <li key={i} className="flex gap-2.5 text-[0.88rem] leading-relaxed text-body">
                        <svg
                          viewBox="0 0 24 24"
                          className="mt-0.5 h-4 w-4 shrink-0 text-emerald-deep"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="mt-5 rounded-lg border border-emerald/30 bg-emerald-tint px-3 py-2 text-[0.82rem] leading-relaxed text-emerald-deep">
                <strong>No fixed legal ratios exist.</strong> The Health and Safety (First-Aid) Regulations 1981
                require provision that is &quot;adequate and appropriate in the circumstances&quot;. The figures
                above are HSE&apos;s suggestions from its needs-assessment guidance — you, as the employer, must
                assess and decide what is enough. This record is your written needs assessment.
              </p>

              <DataReviewedNote date="6 June 2026">
                Suggested figures are from HSE leaflet INDG214 (Table 2) and BS 8599-1; check the current HSE
                guidance before relying on the result.
              </DataReviewedNote>

              <PrintFooter toolName="first aid needs calculator" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="first-aid-needs-calculator" buildText={buildCopyText} label="Copy assessment" />
              <PrintButton tool="first-aid-needs-calculator" label="Print / save assessment as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter your headcount and hazard level to see the suggested first-aiders, appointed persons and kit —
            with adjustments for dispersed sites, lone working and remote locations. The result doubles as your
            written first aid needs assessment.
          </div>
        )}
      </div>
    </div>
  );
}
